import React, { useState } from "react";
import { Button } from "./design-system/Button";
import AddCircleIconBlack from "../../assets/svgIcons/add-circle-icon-black.svg";
import { Checkbox } from "./design-system/Checkbox";
import { DialogFooter } from "./design-system/Dialog";
import type { IntegrationAPIResponse } from "../IntegrationModal";
import AccountListItem from "./AccountListItem";

export interface MessageInfo {
  id: number;
  accountBg: string;
  accountName: string;
  connectOn: string;
  error: boolean;
}

const AccountManagement = ({
  accounts,
  cancelSourceItemsSync,
  handleAddAccountClick,
  handleUploadFilesClick,
  performBulkAction,
  performingAction,
  resyncDataSource,
  revokeDataSource,
}: {
  accounts: IntegrationAPIResponse[];
  handleAddAccountClick: () => void;
  resyncDataSource: (id?: number, bulk?: boolean) => void;
  revokeDataSource: (id?: number, bulk?: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  performBulkAction: (ids: number[], message: string, func: Function) => void;
  performingAction: boolean;
  handleUploadFilesClick: (dataSource?: IntegrationAPIResponse) => void;
  cancelSourceItemsSync: (id?: number) => void;
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState<number[]>([]);

  return (
    <>
      <div className="cc-p-4 cc-min-h-0 cc-flex-grow cc-flex cc-flex-col">
        <div className="cc-flex cc-gap-2 sm:cc-gap-3 cc-mb-[40px] cc-flex-col sm:cc-flex-row">
          <p className="cc-text-xl cc-font-semibold cc-flex-grow dark:cc-text-dark-text-white">
            My Accounts
          </p>
          <div className="cc-flex cc-gap-3 md:cc-gap-[1.75rem]">
            <Button
              className="cc-text-xs !cc-rounded-xl cc-font-semibold cc-shrink-0"
              onClick={() => handleAddAccountClick()}
              size="sm"
              variant="neutral-white"
            >
              <img
                alt="Add Circle Plus"
                className="cc-h-[14px] cc-w-[14px] cc-shrink-0 dark:cc-invert-[1] dark:cc-hue-rotate-180"
                src={AddCircleIconBlack}
              />
              Add Account
            </Button>
          </div>
        </div>
        <div className="cc-flex  cc-flex-col sm:cc-flex-row cc-text-sm cc-font-semibold cc-mb-3 cc-gap-5 sm:cc-gap-3">
          {selectedAccounts.length > 0 ? (
            <button
              className="cc-text-sm cc-font-semibold cc-text-outline-danger_high_em cc-items-start cc-text-left"
              onClick={() => setSelectedAccounts([])}
            >
              Clear selection
            </button>
          ) : (
            <label className="cc-flex cc-gap-2 cc-text-sm cc-font-semibold cc-cursor-pointer dark:cc-text-dark-text-white">
              <Checkbox
                checked={
                  accounts.length
                    ? selectedAccounts.length === accounts.length
                    : false
                }
                className="my-0.5"
                onCheckedChange={() => {
                  const allAccounts = accounts.map((item) => item.id);

                  setSelectedAccounts(allAccounts);
                }}
              />
              Select all
            </label>
          )}
        </div>
        <div
          className="cc-flex cc-flex-col  cc-overflow-y-auto cc-overflow-x-hidden  sm:cc-mx-0 sm:cc-px-0 cc-flex-grow "
          id="scrollableTarget"
        >
          <div className="cc-flex cc-flex-wrap cc-gap-x-[28px]">
            {accounts.map((item) => {
              const isChecked = selectedAccounts.indexOf(item.id) >= 0;

              return (
                <AccountListItem
                  account={item}
                  cancelSourceItemsSync={cancelSourceItemsSync}
                  handleUploadFilesClick={handleUploadFilesClick}
                  isChecked={isChecked}
                  key={item.id}
                  onSelect={() => {
                    setSelectedAccounts((prev) => {
                      if (isChecked) {
                        return prev.filter((id) => id !== item.id);
                      } else {
                        return [...prev, item.id];
                      }
                    });
                  }}
                  resyncDataSource={resyncDataSource}
                />
              );
            })}
          </div>
        </div>
      </div>
      {selectedAccounts.length > 0 && (
        <DialogFooter className="cc-flex cc-justify-between md:cc-flex-col md:cc-gap-2">
          <Button
            className="cc-w-[68%] md:cc-w-full"
            disabled={performingAction}
            onClick={() =>
              performBulkAction(
                selectedAccounts,
                "Finished queuing data sources for resync",
                resyncDataSource,
              )
            }
            size="lg"
            variant="primary"
          >
            Re-sync accounts
          </Button>

          <Button
            className="cc-w-[30%] cc-bg-[#FFE0E0] cc-text-[#F03D3D] md:cc-w-full"
            disabled={performingAction}
            onClick={() =>
              performBulkAction(
                selectedAccounts,
                "Finished revoking your data sources",
                revokeDataSource,
              )
            }
            size="lg"
            variant="secondary"
          >
            Disconnect accounts
          </Button>
        </DialogFooter>
      )}
    </>
  );
};

export default AccountManagement;
